import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Building2, Plus, Pencil, Trash2 } from 'lucide-react';
import { EstablishmentsCard } from '@/components/schools/detail/EstablishmentsCard';
import { EstablishmentFormDialog } from '@/components/schools/EstablishmentFormDialog';
import {
  emptyEstablishmentForm,
  type EstablishmentFormValues,
} from '@/components/schools/establishment-form';
import type { WizardFormData, WizardMode, EstablishmentDraft } from '../types';

const CONTRACT_TYPE_LABELS: Record<string, string> = {
  with_contract: 'Sous contrat',
  without_contract: 'Hors contrat',
};

interface EstablishmentsStepProps {
  mode: WizardMode;
  formData: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
  /** Mode upgradeV2 : code de l'école existante (édition live via l'API) */
  schoolCode?: string;
  hasV1?: boolean;
  hasV2?: boolean;
}

export function EstablishmentsStep({
  mode,
  formData,
  onChange,
  schoolCode,
  hasV1 = false,
  hasV2 = false,
}: EstablishmentsStepProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDraft, setEditingDraft] = useState<EstablishmentDraft | null>(null);

  const header = (
    <div className="text-center mb-8">
      <h2 className="text-xl font-semibold text-foreground">Établissements</h2>
      <p className="text-sm text-muted-foreground mt-1">
        Le RNE, l'académie et l'adresse sont portés par chaque établissement.
        Ils seront recréés sur le tenant lors du déploiement V2.
      </p>
    </div>
  );

  // Mode passage V2 : l'école existe, édition directe via l'API
  // (les établissements V1 sont importés automatiquement au chargement)
  if (mode === 'upgradeV2' && schoolCode) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        {header}
        <Card>
          <CardContent className="p-0">
            <EstablishmentsCard
              schoolCode={schoolCode}
              isEducationNationale={formData.isEducationNationale}
              hasV1={hasV1}
              hasV2={hasV2}
              autoImportV1={hasV1}
              borderless
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Mode création : le principal dérive des informations de l'école,
  // les secondaires sont des brouillons persistés après création
  const drafts = formData.establishments;

  const openCreateDialog = () => {
    setEditingDraft(null);
    setDialogOpen(true);
  };

  const openEditDialog = (draft: EstablishmentDraft) => {
    setEditingDraft(draft);
    setDialogOpen(true);
  };

  const handleSubmit = (values: EstablishmentFormValues) => {
    if (editingDraft) {
      onChange({
        establishments: drafts.map((d) =>
          d.localId === editingDraft.localId ? { ...d, ...draftFromValues(values) } : d
        ),
      });
    } else {
      const nextId = drafts.reduce((max, d) => Math.max(max, d.localId), 0) + 1;
      onChange({
        establishments: [...drafts, { localId: nextId, ...draftFromValues(values) }],
      });
    }
    setDialogOpen(false);
  };

  const handleDelete = (draft: EstablishmentDraft) => {
    onChange({ establishments: drafts.filter((d) => d.localId !== draft.localId) });
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {header}

      {/* Établissement principal — dérivé de l'étape Informations */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4 text-primary" />
            Établissement principal
            <Badge variant="secondary" className="text-xs">Principal</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Nom :</span>
              <span className="ml-2 font-medium">{formData.name || '—'}</span>
            </div>
            {formData.isEducationNationale && (
              <div>
                <span className="text-muted-foreground">RNE :</span>
                <span className="ml-2 font-mono">{formData.rne || '—'}</span>
              </div>
            )}
            {formData.isEducationNationale && (
              <div>
                <span className="text-muted-foreground">Académie :</span>
                <span className="ml-2">{formData.codeAcademie || '—'}</span>
              </div>
            )}
            <div className="col-span-2">
              <span className="text-muted-foreground">Adresse :</span>
              <span className="ml-2">{formData.address || '—'}</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Créé automatiquement à partir des informations de l'école (étape Informations).
          </p>
        </CardContent>
      </Card>

      {/* Établissements secondaires (brouillons) */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4 text-primary" />
              Autres établissements
            </CardTitle>
            <Button size="sm" onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-1" />
              Ajouter
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {drafts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
              <Building2 className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-sm">Aucun établissement secondaire</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  {formData.isEducationNationale && <TableHead>RNE</TableHead>}
                  {formData.isEducationNationale && <TableHead>Académie</TableHead>}
                  <TableHead>Type</TableHead>
                  <TableHead>Adresse</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {drafts.map((draft) => (
                  <TableRow key={draft.localId}>
                    <TableCell className="font-medium">{draft.name}</TableCell>
                    {formData.isEducationNationale && (
                      <TableCell className="font-mono text-sm">
                        {draft.rne || <span className="text-muted-foreground">—</span>}
                      </TableCell>
                    )}
                    {formData.isEducationNationale && (
                      <TableCell className="text-sm">
                        {draft.codeAcademie || <span className="text-muted-foreground">—</span>}
                      </TableCell>
                    )}
                    <TableCell className="text-sm">
                      {draft.contractType ? (
                        CONTRACT_TYPE_LABELS[draft.contractType]
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm max-w-56 truncate">
                      {draft.address || <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => openEditDialog(draft)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(draft)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <p className="text-xs text-muted-foreground mt-3">
            Les établissements secondaires seront créés avec l'école.
          </p>
        </CardContent>
      </Card>

      <EstablishmentFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initialValues={editingDraft ? valuesFromDraft(editingDraft) : emptyEstablishmentForm}
        isEditing={editingDraft !== null}
        isEducationNationale={formData.isEducationNationale}
        allowPrincipalToggle={false}
        onSubmit={handleSubmit}
      />
    </div>
  );
}

function draftFromValues(values: EstablishmentFormValues): Omit<EstablishmentDraft, 'localId'> {
  return {
    name: values.name,
    rne: values.rne,
    codeAcademie: values.codeAcademie,
    address: values.address,
    addressComplement: values.addressComplement,
    latitude: values.latitude,
    longitude: values.longitude,
    contractType: values.contractType,
  };
}

function valuesFromDraft(draft: EstablishmentDraft): EstablishmentFormValues {
  return {
    name: draft.name,
    isPrincipal: false,
    rne: draft.rne,
    codeAcademie: draft.codeAcademie,
    address: draft.address,
    addressComplement: draft.addressComplement,
    latitude: draft.latitude,
    longitude: draft.longitude,
    contractType: draft.contractType,
  };
}
